import React, { useState, useEffect } from 'react';
import { Search, Crosshair, X } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import BASE_URL from '../../config';
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  Box,
  TextField,
  IconButton,
  Typography,
  Checkbox,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import { useOrg } from "../../contexts/OrgContext";


/**
 * TreeList Component
 * 
 * Displays a list of trees for a selected site with filtering capabilities.
 * Tree selection is used to identify grids for task creation (via GridTaskPanel).
 * 
 * Props:
 * - onClose: Function to close the tree list panel
 * - selectedSiteCode: The site ID to fetch trees for
 * - selectedRunId: The run ID to filter trees by
 * - selectedHealthFilter: Health filter ('All', 'Risk', 'Average', 'Good')
 * - onLocateTree: Function to locate a tree on the map (lat, lng, treeId)
 * - onTreeSelectionChange: Callback when selected trees change (for grid filtering)
 */
function TreeList({ 
  onClose, 
  selectedSiteCode,
  selectedSubSiteId,  // Optional sub-site ID
  selectedRunId, 
  selectedHealthFilter, 
  onLocateTree,
  onTreeSelectionChange // Callback to notify parent of selected trees
}) {
  const theme = useTheme();
  const [treeData, setTreeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTreeIds, setSelectedTreeIds] = useState([]);
  const isSelected = (id) => selectedTreeIds.includes(id);
  const { selectedOrgId } = useOrg();
  const [allTreeIds, setAllTreeIds] = useState([]);
  const [allTreeData, setAllTreeData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedTreeIds([]);
    setCurrentPage(1);
  }, [selectedHealthFilter, selectedSiteCode, selectedSubSiteId, selectedRunId]);

  // Notify parent of tree selection changes
  useEffect(() => {
    if (onTreeSelectionChange) {
      const selectedTrees = allTreeData.filter(tree => selectedTreeIds.includes(tree.id));
      onTreeSelectionChange(selectedTreeIds, selectedTrees);
    }
  }, [selectedTreeIds, allTreeData, onTreeSelectionChange]);

  const toggleSelectTree = (id) => {
    setSelectedTreeIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((treeId) => treeId !== id)
        : [...prevSelected, id]
    );
  };

  // Fetch all tree IDs for "select all" functionality
  useEffect(() => {
    const fetchAllTreeData = async () => {
      if (!selectedSiteCode) return;
      const token = await getAuth().currentUser.getIdToken();

      const queryParams = new URLSearchParams({
        ...(selectedSubSiteId ? { sub_site_id: selectedSubSiteId } : { site_id: selectedSiteCode || '' }),
        run_id: selectedRunId || '',
        organisation_id: selectedOrgId
      });

      if (selectedHealthFilter && selectedHealthFilter !== 'All') {
        const healthMap = { Risk: 'low', Average: 'medium', Good: 'high' };
        const health = healthMap[selectedHealthFilter] || selectedHealthFilter.toLowerCase();
        queryParams.append('health', health);
      }

      const res = await fetch(`${BASE_URL}/api/trees?${queryParams}&all=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      const trees = data.trees || [];

      setAllTreeData(trees);
      setAllTreeIds(trees.map(tree => tree.id));
    };

    fetchAllTreeData();
  }, [selectedSiteCode, selectedRunId, selectedHealthFilter, selectedOrgId]);

  // Fetch paginated tree data
  useEffect(() => {
    const fetchTreeData = async () => {
      setLoading(true);
      try {
        const user = getAuth().currentUser;
        const token = user && await user.getIdToken();

        const queryParams = new URLSearchParams({
          ...(selectedSubSiteId ? { sub_site_id: selectedSubSiteId } : { site_id: selectedSiteCode || '' }),
          run_id: selectedRunId || '',
          limit: rowsPerPage.toString(),
          offset: ((currentPage - 1) * rowsPerPage).toString()
        });

        const healthMap = {
          Risk: 'low',
          Average: 'medium',
          Good: 'high'
        };

        if (selectedHealthFilter && selectedHealthFilter !== 'All') {
          const health = healthMap[selectedHealthFilter] || selectedHealthFilter.toLowerCase();
          queryParams.append('health', health);
        }

        const response = await fetch(`${BASE_URL}/api/trees?${queryParams}&organisation_id=${selectedOrgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await response.json();
        if (Array.isArray(data.trees)) {
          setTreeData(data.trees);
          setTotalCount(data.totalCount || 0);
        } else {
          setTreeData([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching tree data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedSiteCode) {
      fetchTreeData();
    }
  }, [selectedSiteCode, selectedRunId, rowsPerPage, currentPage, selectedHealthFilter, selectedOrgId]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const currentData = treeData.filter(tree =>
    tree.id?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
    tree.species?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const areAllSelected = allTreeIds.length > 0 && allTreeIds.every(id => selectedTreeIds.includes(id));

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedTreeIds([]);
    } else {
      setSelectedTreeIds(allTreeIds);
    }
  };

  const getHealthColor = (health) => {
    switch (health?.toLowerCase()) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };


  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header Section */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5, 
        flexWrap: 'wrap',
        bgcolor: 'grey.50'
      }}>
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by ID or Species"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search size={16} style={{ marginRight: 6, color: theme.palette.text.secondary }} />
          }}
          sx={{ flex: 1, minWidth: 150 }}
        />

        {/* Selected count chip */}
        {allTreeIds.length > 0 && (
          <Chip
            label={`${selectedTreeIds.length}/${allTreeIds.length}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        )}

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: 'grey.200',
            '&:hover': { bgcolor: 'grey.300' }
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flex: 1 }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: 'text.secondary', fontSize: '0.875rem' }}>Loading...</Typography>
        </Box>
      ) : (
        <>
          {/* Table */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 350 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50', color: 'text.primary', py: 0.5 }}>
                    <Checkbox
                      checked={areAllSelected}
                      onChange={toggleSelectAll}
                      size="small"
                      sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'primary.main' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Loc</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Tree ID</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Health</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Water</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Species</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', color: 'text.primary', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>Ht</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((tree, index) => (
                  <TableRow
                    key={tree.id}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={isSelected(tree.id)}
                        onChange={() => toggleSelectTree(tree.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {tree.centroid ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const [lng, lat] = tree.centroid.coordinates;
                            onLocateTree?.(lat, lng, tree.id);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <Crosshair size={14} />
                        </IconButton>
                      ) : (
                        <Crosshair size={14} color="#ccc" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                      {tree.id || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                      <Chip
                        label={tree.health || 'N/A'}
                        size="small"
                        color={getHealthColor(tree.health)}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>{tree.water_stress || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>{tree.species || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>{tree.height || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Pagination */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: 1, 
            p: 1.5, 
            borderTop: 1, 
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                Rows:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 50, fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                sx={{ p: 0.5 }}
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                sx={{ p: 0.5 }}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default TreeList;

